import { useMemo } from "react";
import { countBy } from "../utils";

/**
 * Magic-number thresholds used for bucketing firmware freshness and offline duration.
 */
const FW_FRESHNESS_DAYS = { WEEK: 7, MONTH: 30, QUARTER: 90 };
const OFFLINE_HOURS = { ONE: 1, SIX: 6, TWENTY_FOUR: 24 };
const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;

/**
 * Build a binary split dataset (e.g. Online/Offline, Healthy/Faulted).
 * @param {Array} data - Fleet charger array
 * @param {Function} predicate - Boolean filter function
 * @param {string} trueLabel - Label for items passing predicate
 * @param {string} falseLabel - Label for items failing predicate
 * @returns {Array<{ name: string, value: number }>}
 */
const buildBinarySplit = (data, predicate, trueLabel, falseLabel) => {
  if (!data.length) return [];
  const trueCount = data.filter(predicate).length;
  return [
    { name: trueLabel, value: trueCount },
    { name: falseLabel, value: data.length - trueCount },
  ];
};

/**
 * Bucket firmware update timestamps into freshness categories.
 * @param {Array} data - Fleet charger array
 * @returns {Array<{ name: string, value: number }>}
 */
const buildFirmwareFreshness = data => {
  if (!data.length) return [];
  const now = Date.now();
  const buckets = { "< 7 days": 0, "7–30 days": 0, "30–90 days": 0, "> 90 days": 0, Never: 0 };

  data.forEach(d => {
    if (!d.fwUpdatedAt) {
      buckets["Never"]++;
      return;
    }
    const days = (now - new Date(d.fwUpdatedAt).getTime()) / MS_PER_DAY;
    if (days < FW_FRESHNESS_DAYS.WEEK) buckets["< 7 days"]++;
    else if (days < FW_FRESHNESS_DAYS.MONTH) buckets["7–30 days"]++;
    else if (days < FW_FRESHNESS_DAYS.QUARTER) buckets["30–90 days"]++;
    else buckets["> 90 days"]++;
  });

  return Object.entries(buckets)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));
};

/**
 * Bucket offline chargers by downtime duration.
 * @param {Array} data - Fleet charger array
 * @returns {Array<{ name: string, value: number }>}
 */
const buildOfflineDuration = data => {
  if (!data.length) return [];
  const now = Date.now();
  const offline = data.filter(d => !d.isConnected);
  const buckets = { "< 1 hr": 0, "1–6 hr": 0, "6–24 hr": 0, "> 24 hr": 0, "No Data": 0 };

  offline.forEach(d => {
    if (!d.offlineAt) {
      buckets["No Data"]++;
      return;
    }
    const hrs = (now - new Date(d.offlineAt).getTime()) / MS_PER_HOUR;
    if (hrs < OFFLINE_HOURS.ONE) buckets["< 1 hr"]++;
    else if (hrs < OFFLINE_HOURS.SIX) buckets["1–6 hr"]++;
    else if (hrs < OFFLINE_HOURS.TWENTY_FOUR) buckets["6–24 hr"]++;
    else buckets["> 24 hr"]++;
  });

  return Object.entries(buckets)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));
};

/**
 * Build charger status donut data with mutually exclusive buckets.
 * @param {Array} data - Fleet charger array
 * @returns {Array<{ status: string, count: number }>}
 */
const buildChargerStatusDonut = data => {
  if (!data.length) return [];
  let charging = 0;
  let preparing = 0;
  let faulted = 0;
  let offline = 0;
  let online = 0;

  data.forEach(d => {
    if (!d.isConnected) {
      offline++;
    } else if (d.isFaulted) {
      faulted++;
    } else if (d.connectors?.some(c => c.connectorStatusType === "CHARGING")) {
      charging++;
    } else if (d.connectors?.some(c => c.connectorStatusType === "PREPARING")) {
      preparing++;
    } else {
      online++;
    }
  });

  return [
    { status: "charging", count: charging },
    { status: "online", count: online },
    { status: "preparing", count: preparing },
    { status: "offline", count: offline },
    { status: "faulted", count: faulted },
  ].filter(d => d.count > 0);
};

const COMMISSIONING_LABELS = { 0: "Not Commissioned", 1: "Commissioned", 2: "Pending" };
const MODE_LABELS = { 0: "Standard", 1: "Solar", 2: "Off-Peak" };

/**
 * Custom hook that derives all chart datasets from fleet charger data.
 * Separates data transformation logic from UI rendering per §4 — Separation of Concerns.
 *
 * @param {Array} data - Fleet charger array
 * @returns {object} All chart datasets keyed by chart name
 */
export function useFleetChartData(data) {
  const connectivityData = useMemo(() => buildBinarySplit(data, d => d.isConnected, "Online", "Offline"), [data]);

  const faultedData = useMemo(() => buildBinarySplit(data, d => !d.isFaulted, "Healthy", "Faulted"), [data]);

  const offPeakData = useMemo(
    () => buildBinarySplit(data, d => d.isOffPeakEligible, "Eligible", "Not Eligible"),
    [data]
  );

  const dlbData = useMemo(() => buildBinarySplit(data, d => d.dlbEnabled, "DLB Enabled", "DLB Disabled"), [data]);

  const partnerData = useMemo(
    () => buildBinarySplit(data, d => !d.hoControl, "Partner Integrated", "Standalone"),
    [data]
  );

  const offGridData = useMemo(() => buildBinarySplit(data, d => d.isOffGrid, "Off-Grid", "On-Grid"), [data]);

  const modelData = useMemo(() => countBy(data, d => d.modelName || "Unknown"), [data]);
  const fwData = useMemo(() => countBy(data, d => d.fwVersion || "N/A"), [data]);
  const phaseData = useMemo(() => countBy(data, d => (d.phaseCount ? `${d.phaseCount}-Phase` : "Unknown")), [data]);
  const maxCurrentData = useMemo(() => countBy(data, d => (d.maxCurrent ? `${d.maxCurrent}A` : "Unknown")), [data]);
  const skuData = useMemo(() => countBy(data, d => d.sku || "Unknown"), [data]);
  const vendorData = useMemo(() => countBy(data, d => d.vendorName || "Unknown"), [data]);

  const commissioningData = useMemo(() => {
    if (!data.length) return [];
    return countBy(data, d => COMMISSIONING_LABELS[d.commissioningStatus] ?? `Status ${d.commissioningStatus}`);
  }, [data]);

  const modeData = useMemo(() => {
    if (!data.length) return [];
    return countBy(data, d => MODE_LABELS[d.mode] ?? `Mode ${d.mode}`);
  }, [data]);

  const fwFreshnessData = useMemo(() => buildFirmwareFreshness(data), [data]);
  const offlineDurationData = useMemo(() => buildOfflineDuration(data), [data]);
  const chargerStatusDonutData = useMemo(() => buildChargerStatusDonut(data), [data]);

  return {
    connectivityData,
    faultedData,
    offPeakData,
    dlbData,
    partnerData,
    offGridData,
    modelData,
    fwData,
    phaseData,
    maxCurrentData,
    skuData,
    vendorData,
    commissioningData,
    modeData,
    fwFreshnessData,
    offlineDurationData,
    chargerStatusDonutData,
  };
}

export default useFleetChartData;
